import { zodResolver } from '@hookform/resolvers/zod';
import { navigate } from 'gatsby';
import { find, map } from 'ramda';
import React, { FC, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import { CenteredLink } from '@ui/atoms/CenteredLink';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import Listbox, { IListboxOption } from '@ui/forms/Listbox';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import SubmitButton from '@ui/molecules/SubmitButton';

import spaced from '@ui/styled/Spaced';

import { commonLabels } from '@utils/button';

import { IInsuranceProviderSelection } from '@providers/InsuranceProvider';

import { IPageProps } from '@folx-types/flow-steps';
import { IInsuranceProvider } from '@folx-types/insurance';

import theme from '@theme/FolxTheme';

import { schema } from './InsuranceProviderStep.data';

export interface IInsuranceProviderStepPageContent {
	pageHeader: string;
	selectInsuranceLabel: string;
}

interface IInsuranceProviderStepProps extends IPageProps {
	pageContent: IInsuranceProviderStepPageContent;
	insuranceProviders?: IInsuranceProvider[];
	setProvider: (provider: IInsuranceProviderSelection) => void;
}

const SpacedColumn = spaced<IFlexColumn>(FlexColumn);

const PaddedColumn = styled(spaced<IFlexColumn>(FlexColumn))`
	padding-left: ${theme.spacing.md};
	padding-right: ${theme.spacing.md};
`;

const InsuranceProviderStep: FC<IInsuranceProviderStepProps> = ({
	pageContent,
	insuranceProviders = [],
	setProvider,
}) => {
	const [submitting, setSubmitting] = useState(false);
	const { pageHeader, selectInsuranceLabel } = pageContent;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		control,
		formState: { errors },
	} = form;

	const defaultOption: IListboxOption = { value: null, label: 'Search (e.g. Anthem)' };
	const insuranceOptions = useMemo(
		() =>
			map<IInsuranceProvider, IListboxOption>((insuranceProvider) => {
				return { value: insuranceProvider.id, label: insuranceProvider.name };
			}, insuranceProviders),
		[insuranceProviders]
	);

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		const selected = find<IInsuranceProvider>(
			(insuranceProvider) => insuranceProvider.id === data.insuranceProvider,
			insuranceProviders
		);
		if (selected) {
			if (selected.inNetwork) {
				setProvider({ id: selected?.id, name: selected?.name });
				setSubmitting(false);
				navigate(`../details`);
			} else {
				setSubmitting(false);
				navigate(`../out_of_network`);
			}
		} else {
			setSubmitting(false);
		}
	};

	return (
		<CenteredGridItem>
			<form onSubmit={handleSubmit(onSubmit)}>
				<SpacedColumn gap="lgxl" marginBottom="xl">
					<HeadingText>{pageHeader}</HeadingText>
					<Controller
						name="insuranceProvider"
						control={control}
						render={({ field }) => (
							<Listbox
								label={selectInsuranceLabel}
								portal={false}
								options={[defaultOption, ...insuranceOptions]}
								error={!!errors?.insuranceProvider?.message}
								errorLabel={errors?.insuranceProvider?.message}
								{...field}
							/>
						)}
					/>
					<PaddedColumn gap="lgxl" marginBottom="none">
						<SubmitButton variant="secondary" label={commonLabels.next} submitting={submitting} />
						<CenteredLink variant="underline" to="../coverage">
							Cancel
						</CenteredLink>
					</PaddedColumn>
				</SpacedColumn>
			</form>
		</CenteredGridItem>
	);
};

export default InsuranceProviderStep;
